import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Package, Award, FileDown, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { API } from "@/config";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/i18n/LanguageContext";

export default function CustomerAccount() {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [results, setResults] = useState([]);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    axios.get(`${API}/my/orders`, { withCredentials: true }).then((r) => setOrders(r.data)).catch(() => setLoadError(t.enquiry.error));
    axios.get(`${API}/my/exam-results`, { withCredentials: true }).then((r) => setResults(r.data)).catch(() => setLoadError(t.enquiry.error));
  }, []);

  const productName = (id) => t.productItems[id]?.name || id;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div data-testid="customer-account-page" className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2.5" data-testid="account-brand-link">
            <img src="/logo.png" alt="Aadisarvtra logo" className="h-9 w-9 rounded-lg object-cover" />
            <div className="leading-tight">
              <h1 className="font-heading text-base font-bold">{t.account.myAccount}</h1>
              <p className="text-xs text-muted-foreground">{t.account.welcome}, {user?.name || user?.email}</p>
            </div>
          </Link>
          <Button variant="outline" size="sm" onClick={handleLogout} className="gap-2" data-testid="customer-logout-button">
            <LogOut className="h-4 w-4" /> {t.account.logout}
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <div className="flex gap-2">
          <Button variant={tab === "orders" ? "default" : "outline"} size="sm" className="gap-2" data-testid="tab-my-orders" onClick={() => setTab("orders")}>
            <Package className="h-4 w-4" /> {t.account.myOrders}
          </Button>
          <Button variant={tab === "results" ? "default" : "outline"} size="sm" className="gap-2" data-testid="tab-my-results" onClick={() => setTab("results")}>
            <Award className="h-4 w-4" /> {t.account.myResults}
          </Button>
        </div>

        {loadError && (
          <p data-testid="account-load-error" className="mt-4 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {loadError}
          </p>
        )}

        {tab === "orders" ? (
          <div className="mt-6 overflow-x-auto rounded-2xl border border-border bg-card shadow-sm">
            <Table data-testid="my-orders-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t.account.date}</TableHead>
                  <TableHead>{t.account.product}</TableHead>
                  <TableHead>{t.account.amount}</TableHead>
                  <TableHead>{t.account.status}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((o) => (
                  <TableRow key={o.session_id || o.razorpay_order_id} data-testid={`order-row-${o.session_id || o.razorpay_order_id}`}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {new Date(o.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="font-medium">{productName(o.product_id)}</TableCell>
                    <TableCell>₹{o.amount}</TableCell>
                    <TableCell>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${o.payment_status === "paid" ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-amber-500/10 text-amber-600 dark:text-amber-400"}`}>
                        {o.payment_status}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {orders.length === 0 && (
              <div className="p-8 text-center">
                <p data-testid="no-orders-message" className="text-sm text-muted-foreground">{t.account.noOrders}</p>
                <Button asChild variant="outline" size="sm" className="mt-4" data-testid="browse-products-link">
                  <Link to="/#products">{t.nav.products}</Link>
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-6 overflow-x-auto rounded-2xl border border-border bg-card shadow-sm">
            <Table data-testid="my-results-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t.account.date}</TableHead>
                  <TableHead>{t.account.exam}</TableHead>
                  <TableHead>{t.account.score}</TableHead>
                  <TableHead>{t.account.status}</TableHead>
                  <TableHead>{t.account.certificate}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.map((r) => (
                  <TableRow key={r.id} data-testid={`result-row-${r.id}`}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {new Date(r.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="font-medium">{r.exam_title}</TableCell>
                    <TableCell>{r.score}/{r.total} ({r.percent}%)</TableCell>
                    <TableCell>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${r.passed ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-red-500/15 text-red-600 dark:text-red-400"}`}>
                        {r.passed ? t.account.pass : t.account.fail}
                      </span>
                    </TableCell>
                    <TableCell>
                      {r.certificate_no ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1"
                          data-testid={`my-cert-download-${r.id}`}
                          onClick={() => window.open(`${API}/certificates/${r.certificate_no}/pdf`, "_blank")}
                        >
                          <FileDown className="h-3.5 w-3.5" /> {r.certificate_no}
                        </Button>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {results.length === 0 && (
              <div className="p-8 text-center">
                <p data-testid="no-results-message" className="text-sm text-muted-foreground">{t.account.noResults}</p>
                <Button asChild variant="outline" size="sm" className="mt-4" data-testid="browse-exams-link">
                  <Link to="/exams">{t.nav.exam}</Link>
                </Button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
